import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginStart, loginSuccess, loginFailure } from '../../store/slices/authSlice';
import { authAPI } from '../../services/auth.api';

// Import fonts
import '@fontsource/oswald/700.css'; // Bold variant
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';

// Material UI components
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  MenuItem,
  InputLabel,
  styled
} from '@mui/material';
import { Visibility, VisibilityOff, RestaurantMenu } from '@mui/icons-material';

// Custom styled components
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    color: 'rgba(255, 255, 255, 0.9)',
  },
  '& label': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& label.Mui-focused': {
    color: 'rgba(255, 255, 255, 0.9)',
  },
  '& .MuiInput-underline:before': {
    borderBottomColor: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: 'rgba(255, 255, 255, 0.9)',
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: 'rgba(255, 255, 255, 0.9)',
  },
  '& .MuiInputBase-input': {
    color: 'rgba(255, 255, 255, 0.9)',
  },
}));

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    country: 'India',
    dietaryPreferences: []
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const dietaryOptions = [
    'Vegetarian',
    'Vegan',
    'Non-Vegetarian',
    'Gluten-Free',
    'Dairy-Free'
  ];

  const handleChange = (e) => {
    if (e.target.type === 'checkbox') {
      const preferences = [...formData.dietaryPreferences];
      if (e.target.checked) {
        preferences.push(e.target.value);
      } else {
        const index = preferences.indexOf(e.target.value);
        if (index > -1) {
          preferences.splice(index, 1);
        }
      }
      setFormData({
        ...formData,
        dietaryPreferences: preferences
      });
    } else {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value
      });
    }
  };
  
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  
  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      const response = await authAPI.register(formData);
      dispatch(loginStart());
      dispatch(loginSuccess(response.data));
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Registration failed';
      dispatch(loginFailure(errorMessage));
      setError(errorMessage);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/backgroundAKB.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'fixed',
        top: 0,
        left: 0,
        overflowY: 'auto',
        padding: '20px 0',
      }}
    >
      <Container component="main" maxWidth="md" sx={{ position: 'relative', zIndex: 2, marginTop: '25%',maxWidth: '500px' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
        <Box
          sx={{
            p: 3,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'transparent'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <img src="/logoAkb.png" alt="Aaj Kya Banega Logo" width="130" />
          </Box>
          
          <Typography component="h1" variant="h5" 
            sx={{ 
              mb: 1, 
              fontFamily: 'Oswald', 
              fontWeight: 700,
              color: 'white',
              fontSize: '28px',
              letterSpacing: '0.5px',
              textAlign: 'center'
            }}
          >
            Welcome To Aaj Kya Banega
          </Typography>
          
          <Typography variant="body2" textAlign="center" 
            sx={{ 
              mb: 4, 
              fontFamily: 'Roboto',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px'
            }}
          >
            Create your account to get started
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 3, bgcolor: 'rgba(211, 47, 47, 0.7)', color: 'white' }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <StyledTextField
              variant="standard"
              margin="normal"
              required
              fullWidth
              id="name"
              label="Full Name"
              name="name"
              autoComplete="name"
              autoFocus
              value={formData.name}
              onChange={handleChange}
            />
            
            <StyledTextField
              variant="standard"
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              value={formData.username}
              onChange={handleChange}
              helperText="This will be used for logging in"
            />
            
            <StyledTextField
              variant="standard"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
            />
            
            <FormControl fullWidth margin="normal" variant="standard" sx={{ 
                '& .MuiInput-underline:before': {
                  borderBottomColor: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInput-underline:after': {
                  borderBottomColor: 'rgba(255, 255, 255, 0.9)',
                },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                  borderBottomColor: 'rgba(255, 255, 255, 0.9)',
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'rgba(255, 255, 255, 0.9)',
                },
                '& .MuiSelect-select': {
                  color: 'rgba(255, 255, 255, 0.9)',
                },
                '& .MuiSvgIcon-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                }
              }}>
              <InputLabel id="country-label">Country</InputLabel>
              <Select
                labelId="country-label"
                id="country"
                name="country"
                value={formData.country}
                label="Country"
                onChange={handleChange}
              >
                <MenuItem value="India">India</MenuItem>
                <MenuItem value="United States">United States</MenuItem>
                <MenuItem value="Canada">Canada</MenuItem>
                <MenuItem value="United Kingdom">United Kingdom</MenuItem>
                <MenuItem value="Australia">Australia</MenuItem>
                <MenuItem value="Japan">Japan</MenuItem>
                <MenuItem value="China">China</MenuItem>
                <MenuItem value="Singapore">Singapore</MenuItem>
                <MenuItem value="UAE">UAE</MenuItem>
                <MenuItem value="Italy">Italy</MenuItem>
                <MenuItem value="France">France</MenuItem>
                <MenuItem value="Spain">Spain</MenuItem>
                <MenuItem value="Germany">Germany</MenuItem>
                <MenuItem value="Mexico">Mexico</MenuItem>
                <MenuItem value="Brazil">Brazil</MenuItem>
                <MenuItem value="South Africa">South Africa</MenuItem>
              </Select>
            </FormControl>
            
            <StyledTextField
              variant="standard"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              helperText="Must be at least 8 characters long"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <StyledTextField
              variant="standard"
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickShowConfirmPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <FormControl component="fieldset" sx={{ mt: 3, width: '100%' }}>
              <FormLabel component="legend" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontFamily: 'Roboto' }}>Dietary Preferences</FormLabel>
              <FormHelperText sx={{ fontFamily: 'Roboto', color: 'rgba(255, 255, 255, 0.7)' }}>Select all that apply</FormHelperText>
              <FormGroup sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', mt: 1 }}>
                {dietaryOptions.map(option => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={formData.dietaryPreferences.includes(option)}
                        onChange={handleChange}
                        name="dietaryPreferences"
                        value={option}
                        sx={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          '&.Mui-checked': {
                            color: 'rgba(255, 255, 255, 0.9)',
                          },
                        }}
                      />
                    }
                    label={option}
                    sx={{ fontFamily: 'Roboto', color: 'rgba(255, 255, 255, 0.7)' }}
                  />
                ))}
              </FormGroup>
            </FormControl>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ 
                mt: 4, 
                mb: 2, 
                py: 1,
                bgcolor: '#007619',
                '&:hover': {
                  bgcolor: '#007619',
                },
                fontFamily: 'Roboto',
                fontWeight: 400,
                borderRadius: '4px',
                fontSize: '16px',
                textTransform: 'none',
                letterSpacing: '0.5px'
              }}
            >
              Create Account
            </Button>
            
            <Grid container justifyContent="center">
              <Grid item>
                <Typography variant="body2" sx={{ fontFamily: 'Roboto', color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                  Already have an account?{' '}
                  <Link component={RouterLink} to="/login" variant="body2" sx={{ color: '#F9D643', fontWeight: 500, textDecoration: 'none' }}>
                    Sign in instead
                  </Link>
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Container>
    </Box>
  );
};

export default Register;
